import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useAppStore } from '@cya-routes/shared'
import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Header } from '../components/layout/Header'
import { MapView } from '../components/map/MapView'
import { HomeRouteListPanel } from '../components/home/HomeRouteListPanel'
import { getAllRegionData } from '../services/regionFinder'
import { getElevationGradientLevel, getElevationGradientColor, getElevationGradientLabel } from '../services/elevationGradientService'
import { calculateEstimatedTime } from '../utils/timeCalculator'
import { Route, Mountain, Clock } from 'lucide-react'
import { RouteDisplayPage } from '../components/display/RouteDisplayPage'
import { listRoutes, UnifiedRoute, loadRoute } from '../services/firebase/routes'
import { Photo } from '@cya-routes/shared'
import { 
  formatRouteLocation, 
  calculateUnpavedPercentage, 
  getRouteTypeDisplay,
  formatSurfaceDescription,
  getOrganizationName,
  getTotalDistance,
  getTotalElevationGain,
  getTotalElevationLoss,
  getRouteRegion,
  getRouteState
} from '../utils/routeDisplay'
import { ClubSelector } from '../components/layout/ClubSelector'
import { MapLayerControl, MAP_LAYERS } from '../components/map/MapLayerControl'
import { MapControlGroup } from '../components/map/MapControlGroup'
import { ErrorBoundary, MapErrorBoundary } from '../components/ErrorBoundary'
import { RouteLoadModal } from '../components/editor/RouteLoadModal'
import { useNavigate, useLocation } from 'react-router-dom'
import mapboxgl from 'mapbox-gl'
import { getClubBySlug } from '../services/firebase/clubs'
import { getDoc, doc, collection, getDocs } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { Footer } from '../components/layout/Footer'

export function HomePage() {
  const { auth, relationships } = useAppStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { clubSlug } = useParams<{ clubSlug?: string }>()
  const mapRef = useRef<any>(null)
  
  // Extract authentication state
  const isAuthenticated = auth.isAuthenticated
  
  // Check if mobile on initial load
  const getIsMobile = () => window.innerWidth < 768 // md breakpoint in Tailwind
  const [currentSlides, setCurrentSlides] = useState<{[key: string]: number}>({})
  
  // Detect iPhone for special positioning
  const isIPhone = /iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
  const filterBottomPosition = isIPhone 
    ? 'calc(100px + env(safe-area-inset-bottom, 0px))' // More space on iPhone to prevent cutoff
    : 'calc(36px + env(safe-area-inset-bottom, 0px))' // Normal spacing
  
  // Club filter state (for header integration)
  const [selectedClub, setSelectedClub] = useState('all')
  
  // Sidebar sorting state
  const [sortBy, setSortBy] = useState('date')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  
  // TODO: REMOVE LATER - Location filters now handled by HomeRouteListPanel
  // Location filter state (for floating filters)
  const [selectedCountry, setSelectedCountry] = useState('') // Still used by filterRoutes legacy logic
  const [selectedState, setSelectedState] = useState('') // Still used by filterRoutes legacy logic
  // Initialize with null to distinguish between "not initialized" and "no locations selected"
  const [selectedLocations, setSelectedLocations] = useState<Set<string> | null>(null) // Used by HomeRouteListPanel
  const [selectedCountries, setSelectedCountries] = useState<Set<string> | null>(new Set(['Australia'])) // Used by HomeRouteListPanel
  const [selectedRegion, setSelectedRegion] = useState('') // Still used by filterRoutes legacy logic
  const [selectedRegions, setSelectedRegions] = useState<Set<string>>(new Set()) // Used by HomeRouteListPanel
  // const [showCountryDropdown, setShowCountryDropdown] = useState(false)
  // const [showStateDropdown, setShowStateDropdown] = useState(false)
  // const [showRegionDropdown, setShowRegionDropdown] = useState(false)

  // TODO: REMOVE LATER - These redundant filter states are now handled by HomeRouteListPanel
  // Activity and cycling filter state (for floating filters)
  // const [selectedRouteType, setSelectedRouteType] = useState('All types')
  // const [selectedDistance, setSelectedDistance] = useState('All distances')
  const [distanceRange, setDistanceRange] = useState({ min: 0, max: 500 }) // 0-500+ km range - used by HomeRouteListPanel
  // const [selectedTerrain, setSelectedTerrain] = useState('All terrain')
  const [terrainRange, setTerrainRange] = useState({ min: 0, max: 2 }) // 0-2 scale for terrain types - used by filterRoutes
  // const [selectedElevation, setSelectedElevation] = useState('All elevations')
  const [elevationRange, setElevationRange] = useState({ min: 0, max: 4000 }) // 0-4000+ m elevation range - used by HomeRouteListPanel
  // const [elevationGradient, setElevationGradient] = useState(0) // 0-5 scale for gradient difficulty
  const [elevationGradientRange, setElevationGradientRange] = useState({ min: 0, max: 4 }) // Range selection for gradient (5-grade system) - used by HomeRouteListPanel
  const [isLoopFilter, setIsLoopFilter] = useState(false) // used by HomeRouteListPanel
  // const [showRouteTypeDropdown, setShowRouteTypeDropdown] = useState(false)
  // const [showDistanceDropdown, setShowDistanceDropdown] = useState(false)
  // const [showTerrainDropdown, setShowTerrainDropdown] = useState(false)
  // const [showElevationDropdown, setShowElevationDropdown] = useState(false)

  // Route type filter state - used by HomeRouteListPanel
  const [selectedRouteTypes, setSelectedRouteTypes] = useState<string[]>(['Bikepacking', 'Road', 'Mixed', 'Gravel', 'Event', 'Permanent', 'Rail Trail', 'Bike Path Route'])
  
  // Club filter state - used by HomeRouteListPanel
  const [selectedClubs, setSelectedClubs] = useState<string[]>([])
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  
  // Published routes state
  const [publishedRoutes, setPublishedRoutes] = useState<UnifiedRoute[]>([])
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false)
  // Separate state for route photos to avoid triggering marker updates
  const [routePhotos, setRoutePhotos] = useState<Record<string, any[]>>({})
  
  // Route popup card state (for map markers)
  const [selectedRouteCard, setSelectedRouteCard] = useState<any>(null)
  
  // Route Load Modal state
  const [showRouteLoadModal, setShowRouteLoadModal] = useState(false)
  
  // Route cache for lazy loading
  const routeCacheRef = useRef<Map<string, any>>(new Map())

  // Sidebar pagination state
  const INITIAL_ROUTES_COUNT = 20
  const LOAD_MORE_COUNT = 10
  const [displayedRoutesCount, setDisplayedRoutesCount] = useState(INITIAL_ROUTES_COUNT)

  // Map control for zooming to locations
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null)
  const [mapZoom, setMapZoom] = useState<number | null>(null)
  
  // Map layer controls (3D always enabled)
  const [currentLayer, setCurrentLayer] = useState('outdoors')
  const [mapInstance, setMapInstance] = useState<any>(null)
  
  // Remove responsive sidebar behavior - sidebar state is now user-controlled only
  // Removed the useEffect that was closing sidebar on resize
  
  // Handle club slug from URL
  useEffect(() => {
    const loadClubFromSlug = async () => {
      if (clubSlug) {
        console.log('Loading club with slug:', clubSlug)
        try {
          const club = await getClubBySlug(clubSlug)
          if (club) {
            console.log('Found club:', club.name, 'with ID:', club.id)
            setSelectedClubs([club.id])
          } else {
            console.log('No club found with slug:', clubSlug)
            // Optionally redirect to 404 or show error
          }
        } catch (error) {
          console.error('Error loading club by slug:', error)
        }
      }
    }
    
    loadClubFromSlug()
  }, [clubSlug])
  
  // Handle layer change
  const handleLayerChange = (layerId: string) => {
    setCurrentLayer(layerId)
    // MapView will handle the actual layer change internally
  }

  // Function to get coordinates and zoom level for locations
  const getLocationCoordinates = (country?: string, state?: string, region?: string): { center: [number, number]; zoom: number } => {
    // Country-level coordinates and zoom
    const countryCoordinates: Record<string, { center: [number, number]; zoom: number }> = {
      'Australia': { center: [133.7751, -25.2744], zoom: 3 },
      'New Zealand': { center: [174.7633, -40.9006], zoom: 4 }
    }

    // State-level coordinates and zoom
    const stateCoordinates: Record<string, { center: [number, number]; zoom: number }> = {
      'Victoria': { center: [144.9631, -37.4713], zoom: 5 },
      'New South Wales': { center: [147.0863, -32.1656], zoom: 4 },
      'Queensland': { center: [144.7852, -20.9176], zoom: 3 },
      'South Australia': { center: [138.6007, -30.0002], zoom: 4 },
      'Western Australia': { center: [121.4656, -25.0424], zoom: 2 },
      'Tasmania': { center: [146.8087, -42.0409], zoom: 5 },
      'Northern Territory': { center: [133.8807, -19.4914], zoom: 3 },
      'Australian Capital Territory': { center: [149.1300, -35.4735], zoom: 7 },
      'North Island': { center: [175.2878, -38.7755], zoom: 4 },
      'South Island': { center: [170.9864, -43.9503], zoom: 4 }
    }

    // Region-level coordinates (more specific) - matching regionFinder.ts names
    const regionCoordinates: Record<string, { center: [number, number]; zoom: number }> = {
      // Victoria regions
      'Melbourne': { center: [145.15, -37.85], zoom: 7 },
      'Daylesford & the Macedon Ranges': { center: [144.4, -37.3], zoom: 8 },
      'Yarra Valley & Dandenong Ranges': { center: [145.4, -37.8], zoom: 8 },
      'Mornington Peninsula': { center: [145.05, -38.4], zoom: 8 },
      'Phillip Island': { center: [145.25, -38.5], zoom: 9 },
      'Geelong and the Bellarine': { center: [144.45, -38.1], zoom: 8 },
      'Great Ocean Road': { center: [143.0, -38.4], zoom: 6 },
      'Goldfields': { center: [143.75, -37.15], zoom: 7 },
      'Grampians': { center: [141.9, -37.3], zoom: 7 },
      'High Country': { center: [146.9, -36.6], zoom: 6 },
      'Gippsland': { center: [147.8, -38.1], zoom: 6 },
      'Murray': { center: [144.5, -36.1], zoom: 6 },
      
      // NSW regions
      'Blue Mountains': { center: [150.4, -33.6], zoom: 7 },
      'Capital Country': { center: [149.25, -34.75], zoom: 7 },
      'Central Coast': { center: [151.25, -33.25], zoom: 8 },
      'Central NSW': { center: [148.5, -33.0], zoom: 6 },
      'Hunter': { center: [151.15, -32.6], zoom: 7 },
      'Sydney': { center: [151.21, -33.87], zoom: 7 },
      
      // Queensland regions  
      'Brisbane': { center: [153.0, -27.4], zoom: 7 },
      'Gold Coast': { center: [153.3, -28.1], zoom: 8 },
      'Sunshine Coast': { center: [153.1, -26.5], zoom: 8 },
      'Tropical North Queensland': { center: [143.9, -15.5], zoom: 5 },
      'Townsville': { center: [146.25, -19.5], zoom: 7 },
      'Mackay': { center: [148.75, -21.25], zoom: 7 },
      'Whitsundays': { center: [149.0, -20.5], zoom: 8 },
      
      // SA regions
      'Adelaide': { center: [138.5, -34.9], zoom: 7 },
      'Adelaide Hills': { center: [139.0, -34.9], zoom: 8 },
      
      // Other capitals
      'Perth': { center: [115.86, -31.95], zoom: 7 },
      'Hobart': { center: [147.33, -42.88], zoom: 7 },
      'Darwin': { center: [130.85, -12.46], zoom: 7 },
      'Canberra': { center: [149.13, -35.47], zoom: 8 }
    }

    // If region is provided and exists, use region coordinates (most specific)
    if (region && regionCoordinates[region]) {
      return regionCoordinates[region]
    }

    // If state is provided and exists, use state coordinates
    if (state && stateCoordinates[state]) {
      return stateCoordinates[state]
    }

    // If country is provided and exists, use country coordinates (least specific)
    if (country && countryCoordinates[country]) {
      return countryCoordinates[country]
    }

    // Default to Australia if nothing matches
    return { center: [133.7751, -25.2744], zoom: 3 }
  }

  // Get real region data from the region finder service
  const regionData = getAllRegionData()
  const { countries, states, regions } = regionData
  
  // Abbreviation functions
  const getCountryAbbreviation = (country: string): string => {
    const abbreviations: Record<string, string> = {
      'Australia': 'AU',
      'New Zealand': 'NZ'
    }
    return abbreviations[country] || country
  }
  
  const getStateAbbreviation = (state: string): string => {
    const abbreviations: Record<string, string> = {
      'Victoria': 'VIC',
      'New South Wales': 'NSW',
      'Queensland': 'QLD',
      'South Australia': 'SA',
      'Western Australia': 'WA',
      'Tasmania': 'TAS',
      'Northern Territory': 'NT',
      'Australian Capital Territory': 'ACT',
      'North Island': 'NI',
      'South Island': 'SI'
    }
    return abbreviations[state] || state
  }
  
  // Sort data alphabetically
  const sortedCountries = [...countries].sort()
  const sortedStates = states[selectedCountry] ? [...states[selectedCountry]].sort() : []
  const sortedRegions = regions[selectedState] ? [...regions[selectedState]].sort() : []

  // Filter options
  const routeTypeOptions = ['All types', 'Hiking', 'Cycling', 'MTB Park']
  const distanceOptions = ['All distances', '0-5 km', '5-15 km', '15-30 km', '30-50 km', '50+ km']
  const terrainOptions = ['All terrain', 'Road', 'Gravel', 'Mixed']
  
  // Terrain type scale
  const terrainScale = [
    { value: 0, type: 'Road', description: 'Paved roads and bike paths' },
    { value: 1, type: 'Mixed', description: 'Combination of road and gravel' },
    { value: 2, type: 'Gravel', description: 'Dirt roads, gravel paths, trails' }
  ]
  const elevationOptions = ['All elevations', 'Flat (0-100m)', 'Rolling (100-500m)', 'Hilly (500-1000m)', 'Mountainous (1000m+)']
  
  // Elevation gradient scale (m/km)
  const elevationGradientScale = [
    { value: 0, range: '0-10 m/km', perception: 'Flat', example: 'Coastal paths, rail trails' },
    { value: 1, range: '10-20 m/km', perception: 'Rolling', example: 'Countryside loops, mellow road rides' },
    { value: 2, range: '20-35 m/km', perception: 'Hilly', example: 'Most gravel routes, foothills' },
    { value: 3, range: '35-50 m/km', perception: 'Climby', example: 'Mountainous rides, serious road climbs' },
    { value: 4, range: '50-75 m/km', perception: 'Alpine', example: 'Tour de France-type stages' },
    { value: 5, range: '75+ m/km', perception: 'Goat', example: 'Alpe d\'Huez, hill climb events, short + steep loops' }
  ]

  // Format distance display
  const formatDistance = (value: number): string => {
    if (value >= 1000) return '1000+ km'
    if (value === 0) return '0 km'
    return `${value} km`
  }

  // Format distance range display
  const formatDistanceRange = (min: number, max: number): string => {
    const minText = formatDistance(min)
    const maxText = formatDistance(max)
    if (min === 0 && max >= 1000) return 'All distances'
    return `${minText} - ${maxText}`
  }

  // Format elevation display
  const formatElevation = (value: number): string => {
    if (value >= 4000) return '4000+ m'
    if (value === 0) return '0 m'
    return `${value} m`
  }

  // Format elevation range display
  const formatElevationRange = (min: number, max: number): string => {
    const minText = formatElevation(min)
    const maxText = formatElevation(max)
    if (min === 0 && max >= 4000) return 'All elevations'
    return `${minText} - ${maxText}`
  }

  // Format elevation gradient range display
  const formatElevationGradientRange = (min: number, max: number): string => {
    if (min === 0 && max === 5) return 'Any difficulty'
    if (min === max) return elevationGradientScale[min].perception
    return `${elevationGradientScale[min].perception} to ${elevationGradientScale[max].perception}`
  }
  
  // Sorting options
  const sortOptions = [
    { value: 'date', label: 'Date Added' },
    { value: 'distance', label: 'Distance' },
    { value: 'grade', label: 'Grade Rating' },
    { value: 'club', label: 'Club' },
    { value: 'terrain', label: 'Terrain Type' }
  ]
  
  // Filter routes function
  const filterRoutes = (routes: UnifiedRoute[]) => {
    console.log('🔍 filterRoutes called with:', routes.length, 'routes, selectedClubs:', selectedClubs)
    return routes.filter(route => {
      console.log('🔍 Processing route:', route.name)
      
      // Filter out routes hidden from homepage
      if (route.hideFromHomepage) {
        console.log('🚫 Route hidden from homepage:', route.name)
        return false
      }
      
      // Filter by search query
      if (searchQuery) {
        console.log('🔍 Checking search query for:', route.name)
        const query = searchQuery.toLowerCase()
        const routeName = route.name.toLowerCase()
        const routeDescription = (route.description || '').toLowerCase()
        const routeLocation = formatRouteLocation(route).toLowerCase()
        
        if (!routeName.includes(query) && 
            !routeDescription.includes(query) && 
            !routeLocation.includes(query)) {
          console.log('❌ Route filtered out by search:', route.name)
          return false
        }
        console.log('✅ Route passed search filter:', route.name)
      }
      
      console.log('🔍 Checking route type for:', route.name)
      // Filter by route type (with terrain categorization for single routes)
      const routeTypeDisplay = getRouteTypeDisplay(route.routeType)
      
      // For single routes, check terrain-based categorization
      if (routeTypeDisplay === 'Single Route') {
        const unpavedPercentage = calculateUnpavedPercentage(route)
        let categoryToCheck = 'Road'
        if (unpavedPercentage >= 20) categoryToCheck = 'Gravel'
        else if (unpavedPercentage >= 5) categoryToCheck = 'Mixed'
        
        if (!selectedRouteTypes.includes(categoryToCheck)) {
          console.log('❌ Route filtered out by route type (single):', route.name, 'category:', categoryToCheck, 'selected:', selectedRouteTypes)
          return false
        }
      } else {
        // For other route types, check directly
        if (!selectedRouteTypes.includes(routeTypeDisplay)) {
          console.log('❌ Route filtered out by route type (other):', route.name, 'type:', routeTypeDisplay, 'selected:', selectedRouteTypes)
          return false
        }
      }
      console.log('✅ Route passed route type filter:', route.name)
      
      // Filter by distance
      const distance = getTotalDistance(route)
      if (distanceRange.min > 0 || distanceRange.max < 500) {
        console.log('📍 Distance filter active for:', route.name, 'distance:', distance, 'range:', distanceRange)
        // If max is 500, include all routes above 500km (500+)
        if (distanceRange.max >= 500) {
          if (distance < distanceRange.min) {
            console.log('❌ Route filtered out by distance (min):', route.name)
            return false
          }
        } else {
          if (distance < distanceRange.min || distance > distanceRange.max) {
            console.log('❌ Route filtered out by distance (range):', route.name)
            return false
          }
        }
      }
      console.log('✅ Route passed distance filter:', route.name)
      
      // Filter by elevation
      const elevationGain = getTotalElevationGain(route)
      if (elevationRange.min > 0 || elevationRange.max < 4000) {
        console.log('⛰️ Elevation filter active for:', route.name, 'elevation:', elevationGain, 'range:', elevationRange)
        // If max is 4000, include all routes above 4000m (4000+)
        if (elevationRange.max >= 4000) {
          if (elevationGain < elevationRange.min) {
            console.log('❌ Route filtered out by elevation (min):', route.name)
            return false
          }
        } else {
          if (elevationGain < elevationRange.min || elevationGain > elevationRange.max) {
            console.log('❌ Route filtered out by elevation (range):', route.name)
            return false
          }
        }
      }
      console.log('✅ Route passed elevation filter:', route.name)
      
      // Filter by elevation gradient
      if (elevationGradientRange.min > 0 || elevationGradientRange.max < 4) {
        const gradient = getElevationGradientLevel(elevationGain, distance)
        console.log('📈 Gradient filter active for:', route.name, 'gradient:', gradient, 'range:', elevationGradientRange)
        if (gradient < elevationGradientRange.min || gradient > elevationGradientRange.max) {
          console.log('❌ Route filtered out by gradient:', route.name)
          return false
        }
      }
      console.log('✅ Route passed gradient filter:', route.name)
      
      // Filter by clubs (from route list panel club filter) - MOVED BEFORE LOCATION FILTER
      console.log('🎯 Reached club filtering section for route:', route.name)
      console.log('🔍 About to check club filter - selectedClubs:', selectedClubs, 'length:', selectedClubs?.length, 'condition:', selectedClubs && selectedClubs.length > 0)
      if (selectedClubs && selectedClubs.length > 0) {
        const routeData = route as any
        const routeClubId = routeData.submission?.clubId
        console.log('🏢 Checking route:', route.name, 'clubId:', routeClubId, 'selectedClubs:', selectedClubs, 'submission:', routeData.submission)
        if (!routeClubId || !selectedClubs.includes(routeClubId)) {
          console.log('❌ Filtering out route:', route.name, 'because clubId:', routeClubId, 'not in:', selectedClubs)
          return false
        }
        console.log('✅ Keeping route:', route.name)
      }
      
      // Filter by location
      const routeLocation = formatRouteLocation(route)
      const routeState = getRouteState(route)
      const routeRegion = getRouteRegion(route)
      
      console.log('🗺️ About to check location filter for:', route.name, 'selectedLocations:', selectedLocations, 'selectedCountries:', selectedCountries)
      // Filter by location tags if they have been initialized
      if (selectedLocations !== null || selectedCountries !== null) {
        console.log('🗺️ Location filter is active for:', route.name)
        // Get route's location data - prioritize route-level location
        const routeData = route as any
        let locationData = null
        
        // Check route-level location first (new optimized approach)
        if (routeData.location) {
          locationData = routeData.location
        } else if (routeData._statsOnlySegments?.[0]?.location) {
          // Legacy fallback - segments loaded in old version
          locationData = routeData._statsOnlySegments[0].location
        } else if (route.routes?.[0]?.location) {
          // If routes are loaded, use first route's location
          locationData = route.routes[0].location
        } else if (routeData.segments?.[0]?.location) {
          // Another legacy fallback
          locationData = routeData.segments[0].location
        }
        
        if (!locationData) return false
        
        const routeCountry = locationData.country
        const routeState = locationData.state ? {
          'Victoria': 'VIC',
          'New South Wales': 'NSW',
          'Queensland': 'QLD',
          'Western Australia': 'WA',
          'South Australia': 'SA',
          'Tasmania': 'TAS',
          'Northern Territory': 'NT',
          'Australian Capital Territory': 'ACT'
        }[locationData.state] || locationData.state : null
        const routeRegionData = locationData.region || locationData.city || ''
        
        // If countries are selected, check country-level filtering
        if (selectedCountries && selectedCountries.size > 0) {
          if (selectedCountries.has(routeCountry)) {
            // Country is selected - if no states are selected, show all routes in the country
            if (!selectedLocations || selectedLocations.size === 0) {
              // No states selected, check if regions are selected for filtering
              if (selectedRegions && selectedRegions.size > 0) {
                return selectedRegions.has(routeRegionData)
              }
              return true
            }
            // If states are selected, check if route is in selected state
            if (selectedLocations && selectedLocations.size > 0 && routeState) {
              if (selectedLocations.has(routeState)) {
                // State matches, now check regions if any are selected
                if (selectedRegions && selectedRegions.size > 0) {
                  return selectedRegions.has(routeRegionData)
                }
                return true
              }
            }
          }
        }
        
        // Check state-level filtering only
        if (selectedLocations && selectedLocations.size > 0 && routeState) {
          if (selectedLocations.has(routeState)) {
            // State matches, check regions if any are selected
            if (selectedRegions && selectedRegions.size > 0) {
              return selectedRegions.has(routeRegionData)
            }
            return true
          }
        }
        
        return false
      }
      
      // Legacy location filters (for compatibility)
      if (selectedCountry !== 'Australia' && !routeLocation.includes(getCountryAbbreviation(selectedCountry))) {
        return false
      }
      if (selectedState && routeState !== selectedState) {
        return false
      }
      if (selectedRegion && routeRegion !== selectedRegion) {
        return false
      }
      
      // Filter by terrain
      if (terrainRange.min > 0 || terrainRange.max < 2) {
        // Convert unpaved percentage to terrain value (0 = road: 0-5%, 1 = mixed: 5-20%, 2 = gravel: 20-100%)
        const unpavedPercentage = calculateUnpavedPercentage(route)
        let terrainValue = 0
        if (unpavedPercentage >= 20) terrainValue = 2 // Gravel: 20-100%
        else if (unpavedPercentage >= 5) terrainValue = 1 // Mixed: 5-20%
        
        if (terrainValue < terrainRange.min || terrainValue > terrainRange.max) {
          return false
        }
      }
      
      return true
    })
  }
  
  // Lazy load full route data when needed (for map hover)
  const lazyLoadRoute = useCallback(async (routeId: string): Promise<any> => {
    console.log(`🔄 lazyLoadRoute called for ${routeId}`)
    
    // Check cache first
    if (routeCacheRef.current.has(routeId)) {
      console.log(`📦 Returning cached route ${routeId}`)
      return routeCacheRef.current.get(routeId)
    }
    
    try {
      console.log(`🌐 Loading full route data for ${routeId}...`)
      // Load the full route data
      const fullRoute = await loadRoute(routeId, false) // false = full data with coordinates
      if (fullRoute) {
        console.log(`✅ Successfully loaded route ${routeId}`, fullRoute)
        // Cache the result
        routeCacheRef.current.set(routeId, fullRoute)
        
        // Don't update publishedRoutes here - it causes unnecessary re-renders
        // and marker recreation during hover, which loses hover state
        
        return fullRoute
      }
    } catch (error) {
      console.error('❌ Failed to lazy load route:', routeId, error)
    }
    
    return null
  }, [])
  
  // Sort routes function
  const sortRoutes = (routes: UnifiedRoute[]) => {
    // First filter, then sort
    const filteredRoutes = filterRoutes(routes)
    
    return [...filteredRoutes].sort((a, b) => {
      let result = 0
      
      switch (sortBy) {
        case 'distance':
          result = getTotalDistance(a) - getTotalDistance(b)
          break
        case 'grade':
          const gradeA = getElevationGradientLevel(getTotalElevationGain(a), getTotalDistance(a))
          const gradeB = getElevationGradientLevel(getTotalElevationGain(b), getTotalDistance(b))
          result = gradeA - gradeB
          break
        case 'club':
          result = getOrganizationName(a).localeCompare(getOrganizationName(b))
          break
        case 'terrain':
          // For now, use route type as terrain proxy
          result = (a.routeType || '').localeCompare(b.routeType || '')
          break
        case 'date':
        default:
          // Sort by updated date
          result = b.updatedAt.getTime() - a.updatedAt.getTime()
          break
      }
      
      // Apply sort direction
      return sortDirection === 'desc' ? -result : result
    })
  }

  // Format terrain range display
  const formatTerrainRange = (min: number, max: number): string => {
    if (min === 0 && max === 2) return 'All terrain'
    if (min === max) return terrainScale[min].type
    return `${terrainScale[min].type} to ${terrainScale[max].type}`
  }

  // TODO: REMOVE LATER - Dropdown handlers for redundant filters
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      // setShowCountryDropdown(false)
      // setShowStateDropdown(false)
      // setShowRegionDropdown(false)
      // setShowRouteTypeDropdown(false)
      // setShowDistanceDropdown(false)
      // setShowTerrainDropdown(false)
      // setShowElevationDropdown(false)
      setShowSortDropdown(false) // Still needed for sorting dropdown
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])
  
  // Load published routes when club changes
  useEffect(() => {
    const loadRoutes = async () => {
      console.log('🔍 Loading routes for club:', selectedClub)
      setIsLoadingRoutes(true)
      try {
        // Use unified listRoutes function - query by type since that's the primary indicator
        const routes = await listRoutes({
          type: 'published',
          clubId: selectedClub && selectedClub !== 'all' ? selectedClub : undefined,
          limit: 100
        })
        console.log('📊 Loaded routes from Firebase:', routes)
        console.log('🔍 First route submission data:', routes[0]?.submission)
        // Load club data for routes
        const clubIds = [...new Set(routes.map(r => r.submission?.clubId).filter(Boolean))]
        console.log('🏢 Club IDs found in routes:', clubIds)
        const clubData: { [key: string]: any } = {}
        
        // Load all club data in parallel
        await Promise.all(clubIds.map(async (clubId) => {
          try {
            const clubDoc = await getDoc(doc(db, 'clubs', clubId))
            if (clubDoc.exists()) {
              clubData[clubId] = clubDoc.data()
              console.log(`✅ Loaded club data for ${clubId}:`, clubDoc.data())
            } else {
              console.log(`❌ No club found for ID ${clubId}`)
            }
          } catch (error) {
            console.warn(`Failed to fetch club data for ${clubId}:`, error)
          }
        }))
        
        // Add club logos to routes
        const routesWithClubLogos = routes.map(route => ({
          ...route,
          clubLogo: route.submission?.clubId ? clubData[route.submission.clubId]?.logoURL || null : null
        }))
        
        setPublishedRoutes(routesWithClubLogos)
        
        // Load first photo for each route in the background
        loadFirstPhotosForRoutes(routesWithClubLogos)
      } catch (error) {
        console.error('❌ Failed to load routes:', error)
        setPublishedRoutes([])
      } finally {
        setIsLoadingRoutes(false)
      }
    }
    
    loadRoutes()
  }, [selectedClub])
  
  // Track if photos are being loaded to prevent duplicate calls
  const photosLoadingRef = useRef(false)
  
  // Load first photo for each route (for sidebar display)
  const loadFirstPhotosForRoutes = useCallback(async (routes: UnifiedRoute[]) => {
    // Prevent duplicate calls
    if (photosLoadingRef.current) {
      console.log('⏭️ Photo loading already in progress, skipping...')
      return
    }
    
    photosLoadingRef.current = true
    console.log('📸 Loading first photo for each route in background...')
    
    // Process routes in batches to avoid overwhelming the server
    const batchSize = 5
    for (let i = 0; i < routes.length; i += batchSize) {
      const batch = routes.slice(i, i + batchSize)
      
      await Promise.all(batch.map(async (route) => {
        try {
          // Skip if route already has photos
          if (route.photos && route.photos.length > 0) {
            return
          }
          
          // Get photos collection for this route
          const photosSnapshot = await getDocs(collection(db, 'routes', route.id, 'photos'))
          
          if (!photosSnapshot.empty) {
            const allPhotos = photosSnapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() } as Photo))
              .filter(photo => photo.url)
            
            let photoToLoad: Photo | undefined
            
            // Check if route has highlight images
            if ((route as any).highlightImages?.main) {
              // Try to find the main highlight photo
              photoToLoad = allPhotos.find(photo => photo.id === (route as any).highlightImages.main)
              console.log(`📸 Loading main highlight photo for route ${route.name}`)
            }
            
            // If no main highlight or not found, try first additional highlight
            if (!photoToLoad && (route as any).highlightImages?.additional && (route as any).highlightImages.additional.length > 0) {
              const firstAdditionalId = (route as any).highlightImages.additional[0]
              photoToLoad = allPhotos.find(photo => photo.id === firstAdditionalId)
              console.log(`📸 Loading additional highlight photo for route ${route.name}`)
            }
            
            // Fall back to first photo with URL
            if (!photoToLoad && allPhotos.length > 0) {
              photoToLoad = allPhotos[0]
              console.log(`📸 Loading first available photo for route ${route.name}`)
            }
            
            if (photoToLoad) {
              // Update the route in state with the photo
              setPublishedRoutes(prev => prev.map(r => 
                r.id === route.id 
                  ? { ...r, photos: [photoToLoad!] }
                  : r
              ))
            }
          }
        } catch (error) {
          console.warn(`Failed to load photo for route ${route.id}:`, error)
        }
      }))
      
      // Small delay between batches to avoid rate limiting
      if (i + batchSize < routes.length) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }
    
    console.log('✅ Finished loading first photos for routes')
    photosLoadingRef.current = false
  }, [])
  
  // Reset pagination when routes or filters change
  useEffect(() => {
    setDisplayedRoutesCount(INITIAL_ROUTES_COUNT)
  }, [publishedRoutes, selectedClub, sortBy, sortDirection])
  


  // Get the filtered routes for both sidebar and map
  const filteredAndSortedRoutes = useMemo(() => {
    const result = sortRoutes(publishedRoutes)
    console.log('🗺️ Filtered routes for map:', result.length, 'out of', publishedRoutes.length, 'total routes')
    console.log('🏢 Current selectedClubs:', selectedClubs)
    return result
  }, [publishedRoutes, selectedLocations, selectedCountry, selectedState, selectedRegion, selectedRegions, selectedRouteTypes, distanceRange, elevationRange, elevationGradientRange, terrainRange, isLoopFilter, sortBy, sortDirection, searchQuery, selectedClubs])

  // Slider functions
  const nextSlide = useCallback((routeId: string) => {
    const route = publishedRoutes.find(r => r.id === routeId)
    if (!route || !route.photos || route.photos.length === 0) return
    
    setCurrentSlides(prev => ({
      ...prev,
      [routeId]: ((prev[routeId] || 0) + 1) % route.photos.length
    }))
  }, [publishedRoutes])

  const prevSlide = useCallback((routeId: string) => {
    const route = publishedRoutes.find(r => r.id === routeId)
    if (!route || !route.photos || route.photos.length === 0) return
    
    setCurrentSlides(prev => ({
      ...prev,
      [routeId]: ((prev[routeId] || 0) - 1 + route.photos.length) % route.photos.length
    }))
  }, [publishedRoutes])

  const setSlide = useCallback((routeId: string, slideIndex: number) => {
    setCurrentSlides(prev => ({
      ...prev,
      [routeId]: slideIndex
    }))
  }, [])
  
  // Handle route card click - open popup instead of full modal
  const handleRouteClick = async (route: UnifiedRoute) => {
    console.log('🎯 Route clicked:', route.name)
    
    // Pan to route location if we have coordinates
    const coordinates = route.coordinates?.[0] || route.routes?.[0]?.coordinates?.[0]
    if (mapInstance && coordinates) {
      const [lng, lat] = coordinates
      const isMobile = window.innerWidth < 640
      
      // On mobile, offset the center to the left so marker appears on left side
      if (isMobile) {
        // Calculate offset to position marker on left 1/4 of screen
        const mapContainer = mapInstance.getContainer()
        const width = mapContainer.offsetWidth
        const offsetX = width * 0.25 // 25% from left
        
        mapInstance.easeTo({
          center: [lng, lat],
          duration: 1000,
          offset: [-width * 0.25, 0], // Negative offset moves center to the right, placing marker on left
          padding: { top: 50, bottom: 50, left: 20, right: 20 }
        })
      } else {
        mapInstance.easeTo({
          center: [lng, lat],
          duration: 1000,
          padding: { top: 100, bottom: 100, left: 100, right: 100 }
        })
      }
    }
    
    // Load route data for popup
    let fullRouteData = route
    try {
      console.log('📸 Loading route data for popup...')
      // Always load full data for popup to ensure we have photos
      const loadedRoute = await loadRoute(route.id, false) // false = load full data including photos
      if (loadedRoute) {
        fullRouteData = loadedRoute
        console.log(`📸 Loaded route data:`, {
          routeId: route.id,
          hasPhotos: !!loadedRoute.photos,
          photosLength: loadedRoute.photos?.length || 0,
          firstPhoto: loadedRoute.photos?.[0],
          routeType: loadedRoute.type,
          lightweight: false
        })
      }
    } catch (error) {
      console.warn('Failed to load full route data for popup:', error)
    }
      
      // Transform route data for popup card (same format as map markers)
      const routeData = {
        name: route.name,
        routeType: getRouteTypeDisplay(route.routeType),
        coordinates: fullRouteData.coordinates || [],
        location: formatRouteLocation(route),
        region: getRouteRegion(route),
        distance: getTotalDistance(route),
        elevationGain: getTotalElevationGain(route),
        surface: formatSurfaceDescription(route),
        unpavedPercentage: calculateUnpavedPercentage(route),
        description: fullRouteData.description || '',
        creator: route.attribution?.name || route.ownerDisplayName || route.ownerId,
        creatorPhoto: route.ownerPhoto || null,
        organization: getOrganizationName(route),
        clubLogo: (route as any).clubLogo || null,
        images: (() => {
          const photos: string[] = []
          // Process photos from full route data
          if (fullRouteData.photos && fullRouteData.photos.length > 0) {
            console.log(`📸 Popup - Found ${fullRouteData.photos.length} total photos`)
            
            // First, add highlight images in order
            if (fullRouteData.highlightImages?.main) {
              const mainPhoto = fullRouteData.photos.find((p: any) => p.id === fullRouteData.highlightImages!.main)
              if (mainPhoto?.url) {
                photos.push(mainPhoto.url)
                console.log('📸 Added main highlight image')
              }
            }
            
            if (fullRouteData.highlightImages?.additional) {
              fullRouteData.highlightImages.additional.forEach((photoId: string) => {
                const photo = fullRouteData.photos.find((p: any) => p.id === photoId)
                if (photo?.url && photos.length < 5) {
                  photos.push(photo.url)
                  console.log('📸 Added additional highlight image')
                }
              })
            }
            
            // If we need more photos to reach 5, add random ones
            if (photos.length < 5) {
              const photosWithUrls = fullRouteData.photos.filter((photo: any) => 
                photo.url && !photos.includes(photo.url)
              )
              
              const remaining = 5 - photos.length
              if (photosWithUrls.length > remaining) {
                const shuffled = [...photosWithUrls].sort(() => 0.5 - Math.random())
                shuffled.slice(0, remaining).forEach((photo: any) => {
                  photos.push(photo.url)
                })
                console.log(`📸 Added ${remaining} random photos to reach 5`)
              } else {
                // If fewer photos available, add all
                photosWithUrls.forEach((photo: any) => {
                  photos.push(photo.url)
                })
              }
            }
          }
          // Filter out static map images
          const filtered = photos.filter(img => 
            !img.includes('staticmap') && 
            !img.includes('static-map') && 
            !img.includes('maps.googleapis.com') &&
            !img.includes('mapbox.com')
          )
          console.log(`📸 Popup - Final filtered photos: ${filtered.length}`)
          
          // Return filtered photos directly - no thumbnail fallback
          // The RoutePopupCard will handle showing thumbnail only when there are truly no photos
          return filtered
        })(),
        thumbnail: route.thumbnail || null,
        firebaseRoute: fullRouteData // TODO: Remove this once RoutePopupCard is updated
      }
      
    // Set for popup AND map route rendering  
    setSelectedRouteCard(routeData)
    console.log('Setting selectedRoute with:', routeData)
    
    // Pan to route center
    if (fullRouteData.coordinates && fullRouteData.coordinates.length > 0) {
      const coordinates = fullRouteData.coordinates
      
      // Calculate center point of the route
      let totalLng = 0
      let totalLat = 0
      
      coordinates.forEach(([lng, lat]: [number, number]) => {
        totalLng += lng
        totalLat += lat
      })
      
      const centerLng = totalLng / coordinates.length
      const centerLat = totalLat / coordinates.length
      
      // Pan map to route center
      if (mapInstance) {
        mapInstance.easeTo({
          center: [centerLng, centerLat],
          duration: 1000 // Smooth animation
        })
      }
    }
  }
  
  // Load photos when a route is selected from the map marker
  useEffect(() => {
    const loadPhotosForSelectedRoute = async () => {
      if (selectedRouteCard && selectedRouteCard.id && selectedRouteCard.images.length === 0) {
        console.log('🗺️ Route selected from marker, loading photos...')
        try {
          const loadedRoute = await loadRoute(selectedRouteCard.id, false) // false = load full data including photos
          if (loadedRoute) {
            console.log(`📸 Loaded ${loadedRoute.photos?.length || 0} photos for marker popup`)
            
            // Update the route data with loaded photos
            const photos: string[] = []
            if (loadedRoute.photos && loadedRoute.photos.length > 0) {
              // First, add highlight images in order
              if (loadedRoute.highlightImages?.main) {
                const mainPhoto = loadedRoute.photos.find((p: any) => p.id === loadedRoute.highlightImages!.main)
                if (mainPhoto?.url) {
                  photos.push(mainPhoto.url)
                  console.log('📸 Marker popup - Added main highlight image')
                }
              }
              
              if (loadedRoute.highlightImages?.additional) {
                loadedRoute.highlightImages.additional.forEach((photoId: string) => {
                  const photo = loadedRoute.photos.find((p: any) => p.id === photoId)
                  if (photo?.url && photos.length < 5) {
                    photos.push(photo.url)
                    console.log('📸 Marker popup - Added additional highlight image')
                  }
                })
              }
              
              // If we need more photos to reach 5, add random ones
              if (photos.length < 5) {
                const photosWithUrls = loadedRoute.photos.filter((photo: any) => 
                  photo.url && !photos.includes(photo.url)
                )
                
                const remaining = 5 - photos.length
                if (photosWithUrls.length > remaining) {
                  const shuffled = [...photosWithUrls].sort(() => 0.5 - Math.random())
                  shuffled.slice(0, remaining).forEach((photo: any) => {
                    photos.push(photo.url)
                  })
                  console.log(`📸 Marker popup - Added ${remaining} random photos to reach 5`)
                } else {
                  // If fewer photos available, add all
                  photosWithUrls.forEach((photo: any) => {
                    photos.push(photo.url)
                  })
                }
              }
            }
            
            // Filter out static map images
            const filtered = photos.filter(img => 
              !img.includes('staticmap') && 
              !img.includes('static-map') && 
              !img.includes('maps.googleapis.com') &&
              !img.includes('mapbox.com')
            )
            
            // Update the selected route card with photos
            setSelectedRouteCard({
              ...selectedRouteCard,
              images: filtered,
              firebaseRoute: loadedRoute
            })
          }
        } catch (error) {
          console.warn('Failed to load photos for marker popup:', error)
        }
      }
    }
    
    loadPhotosForSelectedRoute()
  }, [selectedRouteCard?.id])

  // Handle viewing full route from popup card
  const handleViewFullRoute = async (routeData: any) => {
    console.log('View full route clicked:', routeData)
    console.log('Auth state:', { isAuthenticated, hasCurrentUser: !!auth?.currentUser, userId: auth?.currentUser?.uid })
    
    // Navigate to the route page - no authentication required for viewing published routes
    const routeId = routeData.firebaseRoute?.id || routeData.id
    const slug = routeData.firebaseRoute?.slug || routeData.slug
    
    if (slug) {
      // Use slug-based URL if available
      navigate(`/route/${slug}`)
    } else if (routeId) {
      // Fall back to ID-based URL
      navigate(`/route/${routeId}`)
    }
  }


  return (
    <>
      <Helmet>
        <title>CYA Routes - Discover Epic Bike Trails</title>
        <meta name="description" content="Explore curated gravel, road, and mountain bike routes across Australia. Find your next cycling adventure with detailed maps, elevation profiles, and local insights." />
      </Helmet>
      
      <div className="flex flex-col h-screen bg-white">
        <style>{`
        .range-slider::-webkit-slider-thumb {
          appearance: none;
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          border: 2px solid white;
          pointer-events: all;
        }
        .range-slider::-moz-range-thumb {
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          pointer-events: all;
        }
        .range-slider {
          pointer-events: none;
        }
        .range-slider::-webkit-slider-thumb {
          pointer-events: all;
        }
        .range-slider::-moz-range-thumb {
          pointer-events: all;
        }
      `}</style>
      <Header onLoadRoutes={() => {
        console.log('Load Routes clicked, opening modal...')
        setShowRouteLoadModal(true)
      }} />
      
      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Map Area - Full width */}
        <div className="absolute inset-0">
          <MapErrorBoundary>
            <MapView 
              ref={mapRef}
              className="w-full h-full" 
              mapCenter={mapCenter}
              mapZoom={mapZoom}
              selectedClub={selectedClub}
              onClubChange={setSelectedClub}
              showSampleRoutes={false}
              showMapControls={false} // We're using separate MapControlGroup
              currentLayer={currentLayer}
              onLayerChange={handleLayerChange}
              publishedRoutes={filteredAndSortedRoutes}
              onRouteMarkerClick={handleViewFullRoute}
              selectedRouteCard={selectedRouteCard}
              onRouteCardSelect={setSelectedRouteCard}
              onMapUpdate={() => {
                setMapCenter(null)
                setMapZoom(null)
              }}
              userMemberships={relationships.memberships}
              onMapReady={setMapInstance}
              onLazyLoadRoute={lazyLoadRoute}
            />
          </MapErrorBoundary>
          
          
          {/* Grouped Map Controls - Below Zoom Controls */}
          <div className="absolute z-30" style={{ top: '175px', right: '10px' }}>
            <MapControlGroup 
              map={mapInstance}
              currentLayer={currentLayer}
              onLayerChange={handleLayerChange}
            />
          </div>
        </div>
        
        {/* Floating Route List Panel */}
        <HomeRouteListPanel
          routes={isLoadingRoutes ? [] : filteredAndSortedRoutes}
          allRoutes={publishedRoutes}
          onRouteClick={handleRouteClick}
          selectedRouteId={selectedRouteCard?.id}
          filters={{
            country: selectedCountry,
            state: selectedState,
            region: selectedRegion,
            routeTypes: selectedRouteTypes,
            distance: distanceRange,
            terrain: terrainRange,
            elevation: elevationRange,
            elevationGradient: elevationGradientRange,
            isLoop: isLoopFilter,
            clubs: selectedClubs
          }}
          onFiltersChange={(newFilters) => {
            if (newFilters.country !== undefined) setSelectedCountry(newFilters.country)
            if (newFilters.state !== undefined) setSelectedState(newFilters.state)
            if (newFilters.region !== undefined) setSelectedRegion(newFilters.region)
            if (newFilters.routeTypes !== undefined) setSelectedRouteTypes(newFilters.routeTypes)
            if (newFilters.distance !== undefined) setDistanceRange(newFilters.distance)
            if (newFilters.terrain !== undefined) setTerrainRange(newFilters.terrain)
            if (newFilters.elevation !== undefined) setElevationRange(newFilters.elevation)
            if (newFilters.elevationGradient !== undefined) setElevationGradientRange(newFilters.elevationGradient)
            if (newFilters.isLoop !== undefined) setIsLoopFilter(newFilters.isLoop)
            if (newFilters.clubs !== undefined) {
              console.log('🏢 Club filter changed:', newFilters.clubs)
              setSelectedClubs(newFilters.clubs)
            }
          }}
          isSearching={isLoadingRoutes}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          selectedClub={selectedClub}
          selectedLocations={selectedLocations}
          onLocationsChange={(locations, zoomToState) => {
            const previousLocations = selectedLocations || new Set()
            setSelectedLocations(locations)
            
            // Define state bounds for zooming
            const stateBounds: { [key: string]: [[number, number], [number, number]] } = {
              'VIC': [[140.9617, -39.1592], [149.9766, -33.9806]],
              'NSW': [[140.9993, -37.5051], [153.6383, -28.1570]],
              'QLD': [[138.0000, -29.0000], [153.5522, -10.6852]],
              'WA': [[112.9211, -35.1348], [129.0019, -13.6895]],
              'SA': [[129.0019, -38.0625], [141.0019, -25.9960]],
              'TAS': [[143.8206, -43.6607], [148.4786, -39.5739]],
              'NT': [[129.0019, -25.9986], [138.0019, -10.9661]],
              'ACT': [[148.7630, -35.9207], [149.3995, -35.1244]]
            }
            
            // If zoomToState is provided (when activating), zoom to it
            if (zoomToState && stateBounds[zoomToState] && mapRef.current) {
              mapRef.current.fitBounds(stateBounds[zoomToState], { padding: 50 })
            } 
            // If only one location remains after deactivation, zoom to it
            else if (locations.size === 1 && previousLocations.size > locations.size) {
              const remainingState = Array.from(locations)[0]
              if (stateBounds[remainingState] && mapRef.current) {
                mapRef.current.fitBounds(stateBounds[remainingState], { padding: 50 })
              }
            }
            // If no states selected but Australia is selected, zoom to Australia
            else if (locations.size === 0 && selectedCountries && selectedCountries.has('Australia') && mapRef.current) {
              const australiaBounds: [[number, number], [number, number]] = [[112.9211, -43.6607], [153.6383, -10.6852]]
              mapRef.current.fitBounds(australiaBounds, { padding: 50 })
            }
          }}
          selectedCountries={selectedCountries}
          onCountriesChange={setSelectedCountries}
          selectedRegions={selectedRegions}
          onRegionsChange={setSelectedRegions}
        />
      </div>
      
      
      {/* Route Load Modal */}
      {showRouteLoadModal && (
        <RouteLoadModal
          isOpen={showRouteLoadModal}
          onClose={() => {
            console.log('Closing route load modal')
            setShowRouteLoadModal(false)
          }}
          onLoadRoute={(routes, draftInfo) => {
            console.log('Route selected, navigating to editor with:', { routes, draftInfo })
            // Navigate to editor page with the loaded route data
            navigate('/editor', { 
              state: { 
                loadedRoutes: routes,
                draftInfo: draftInfo 
              } 
            })
          }}
        />
      )}
      
      {/* Legal Footer - Floating at bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-10 bg-gray-900/90 backdrop-blur-sm border-t border-gray-800">
        <div className="px-4 py-2 flex items-center justify-between text-xs text-gray-400">
          <div>© {new Date().getFullYear()} CYARoutes</div>
          <div className="flex gap-4">
            <a href="/privacy-policy" className="hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:text-white transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}
